'use client';

import { useTrialForm } from '@/components/_rebrand/TrialForm/TrialForm.hooks';
import gtm from '@/lib/gtm';
import { isBrowser } from '@/lib/utils';
import { FC } from 'react';
import { TextCombination } from '../TextCombination';
import ConfirmationCodeForm from './ConfirmationCodeForm';
import { ManagedTrialConfirmation } from './ManagedTrialConfirmation';
import EmailPasswordForm from './TrialEmailPasswordForm';
import { TrialModalContainer } from './TrialModalContainer';
import TrialPersonalInfoForm from './TrialPersonalInfoForm';

type SignupModalProps = {
  preventClose?: boolean;
  onClose?: () => void;
  step1Heading?: string;
  step1Subheading?: string;
  step2Heading?: string;
  step2Subheading?: string;
  step3Heading?: string;
  step3Subheading?: string;
}

export type SignupModalStep = {
  heading?: string;
  subheading?: string;
}

const SignupModal: FC<SignupModalProps> = ({
  preventClose = false,
  onClose = () => { },
  step1Heading = 'Try Front for free',
  step1Subheading = 'Help us tailor the trial experience to your needs.',
  step2Heading = '14-days free, no credit card required',
  step2Subheading = 'You’re one step closer to delivering exceptional service at scale.',
  step3Heading = 'You’re almost there!',
  step3Subheading = 'We sent a six-digit code to your email. It will expire in 30 minutes. Keep this window open while checking your code.',
}) => {
  const { currentStep,
    showLoader,
    emailState,
    error,
    personalInfoFormik,
    emailPasswordFormik,
    confirmationCodeFormik,
    gdprOptIn } = useTrialForm();

  const urlParams = isBrowser() ? new URLSearchParams(window.location.search) : new URLSearchParams();
  // If override is true, proceed to self-service trial flow regardless of company size
  const overrideAlternateFlow = urlParams.get('override') === 'true';
  // If affiliate is true, proceed to self-service trial flow regardless of company size and replace '14-day' limit copy 
  const isAffiliate = urlParams.get('affiliate');
  // Values for passing between steps

  return (
    // Overflow visible required for the password form's tooltip to be visible
    <TrialModalContainer
      name="Free Trial Modal"
      preventClose={preventClose}
      backgroundImageSrc="https://front.com/assets/pages/global-ai/Try-for-Free-Modal/Inbox-Background.png"
      onClose={() => {
        onClose();
      }}>
      {currentStep === 1 && (
        <>
          <TextCombination
            type="h3p2"
            align="left"
            title={overrideAlternateFlow ? 'Create your free account' : step1Heading}
            description={overrideAlternateFlow ? 'Sign up for a 14-day free trial, no credit card required.' : step1Subheading}
          />
          <TrialPersonalInfoForm
            formik={personalInfoFormik}
            gdprOptIn={gdprOptIn}
            emailState={emailState}
          />
        </>
      )}

      {currentStep === 2 && (
        <ManagedTrialConfirmation
          onDemoClick={() => {
            gtm.track('click', {
              element_type: 'link',
              element_label: 'Product Tour',
              location: `Form Success - Managed Trial Confirmation Modal`,
            });
            onClose();
          }} />
      )}

      {currentStep === 3 && (
        <>
          <TextCombination
            type="h3p2"
            align="left"
            title={!!isAffiliate ? 'Develop on Front' : step2Heading}
            description={step2Subheading}
          />
          <EmailPasswordForm
            formik={emailPasswordFormik}
          />
        </>
      )}

      {currentStep === 4 && (
        <>
          <TextCombination
            type="h3p2"
            align="left"
            title={step3Heading}
            description={step3Subheading}
          />
          <ConfirmationCodeForm
            formik={confirmationCodeFormik}
            showLoader={showLoader}
            error={error}
          />
        </>
      )}
    </TrialModalContainer>
  );
};

export default SignupModal;
